import { Component, Input, OnInit, signal } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { GenericWarningDialogData, StandardLocation, RidePreparationLog } from 'src/app/interfaces';
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, addDoc } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { LogType } from 'src/app/enums';
import { ucFirst } from 'src/app/globals';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import {
  EditRidePreparationItemDialogComponent,
  Field,
} from '../../dialogs/edit-ride-preparation-item-dialog/edit-ride-preparation-item-dialog.component';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-standard-locations',
  templateUrl: './standard-locations.component.html',
  styleUrl: './standard-locations.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    MatProgressSpinner,
  ],
})
export class StandardLocationsComponent implements OnInit {
  @Input() parentData: { processing: boolean };

  displayedColumns = ['name', 'address', 'button'];
  standardLocations = signal<StandardLocation[]>([]);
  filteredStandardLocations = signal<StandardLocation[]>([]);
  totalStandardLocations = signal<number>(0);
  searchCtrl = new UntypedFormControl();
  organisationId = dataStore.selectedOrganisation.id;

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    onSnapshot(
      query(collection(db, `organisations/${this.organisationId}/addresses`), orderBy('name')),
      (snapshot) => {
        const standardLocations: StandardLocation[] = [];
        this.totalStandardLocations.set(snapshot.size);
        snapshot.forEach((doc) => {
          const data = doc.data() as StandardLocation;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          standardLocations.push({ id, ...data });
        });
        this.standardLocations.set(standardLocations);
        this.filteredStandardLocations.set(
          this.filterStandardLocations(this.searchCtrl.value, standardLocations),
        );
      },
    );
    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredStandardLocations.set(
        this.filterStandardLocations(value, this.standardLocations()),
      );
    });
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  filterStandardLocations(value: string, array: StandardLocation[]): StandardLocation[] {
    if (!value || value === '') {
      return array;
    }
    const filterValue = value.toLowerCase();
    return array.filter(
      (location) =>
        location.name.toLowerCase().includes(filterValue) ||
        location.address.toLowerCase().includes(filterValue),
    );
  }

  editItem(standardLocation?: StandardLocation) {
    const fields: Field[] = [
      {
        key: 'name',
        type: 'string',
        label: 'Locatienaam',
        required: true,
      },
      {
        key: 'address',
        type: 'addressAutocomplete',
        label: 'Adres',
        required: true,
      },
    ];
    const dialogRef = this.dialog.open(EditRidePreparationItemDialogComponent, {
      width: '400px',
      data: {
        item: standardLocation,
        itemLabel: 'standaardlocatie',
        fields: fields,
      },
    });
  }

  deleteItem(standardLocation: StandardLocation) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deleteStandardLocation',
        optionalData: {
          addressName: standardLocation.name,
          deleteMode: true,
        },
      } as GenericWarningDialogData,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await deleteDoc(
          doc(db, `organisations/${this.organisationId}/addresses/${standardLocation.id}`),
        );
        await this.logDelete(standardLocation);
        this.snackBar.open('De standaardlocatie is verwijderd.', 'x', {
          duration: 5000,
        });
      }
    });
  }

  getExportData() {
    const standardLocations = this.standardLocations().map(
      (location) => location as StandardLocation,
    );
    const columns = [
      { name: 'Naam', itemProperty: 'name' },
      { name: 'Adres', itemProperty: 'address' },
    ];
    return { items: standardLocations, columns: columns, options: { sortBy: 'Naam' } };
  }

  getImportConfig() {
    const columnSettings = [
      { columnName: 'naam', itemProperty: 'name', required: true },
      { columnName: 'adres', itemProperty: 'address', required: true },
    ];
    const existingData = this.standardLocations().map((location) => location as StandardLocation);

    return {
      tab: 'standardLocations',
      uniqueKey: 'name',
      organisationId: this.organisationId,
      columnSettings: columnSettings,
      existingData: existingData,
    };
  }

  async logDelete(item: StandardLocation) {
    if (!item) {
      return;
    }

    const logObj: RidePreparationLog = {
      type: LogType.RidePreparationStandardLocation,
      date: new Date(),
      action: 'delete',
      userEmail: dataStore.rootUser.email,
      userId: dataStore.rootUser.id,
      itemType: 'standaardlocatie',
      itemId: item.id,
      role:
        dataStore.rootUser.rights === 'admin'
          ? 'systemAdmin'
          : dataStore.orgUser?.roles?.join(', ') || 'onbekend',
      before: { ...item },
      after: null,
    };
    delete logObj.before.id;

    await addDoc(
      collection(
        db,
        `eventLogs/${dataStore.selectedOrganisation.id}/changedRidePreparationSettings`,
      ),
      logObj,
    );
  }
}
